/**
 * Open-Meteo current-conditions client.
 *
 * A no-key public forecast API, used to prefill the weather go/no-go with a
 * real observation instead of a hand-typed guess. It is a THIRD-PARTY host, so
 * unlike @/api/client this deliberately does not go through the app's own
 * request pipeline (no bearer token, no credentials, not in the activity log).
 * It never throws and never blocks: a failure — offline, rate-limited, or
 * blocked by the app's CSP, which does not list this host — comes back as a
 * typed error the panel shows, leaving the manual fields fully usable.
 */

const ENDPOINT = 'https://api.open-meteo.com/v1/forecast';

export interface OpenMeteoConditions {
  windSpeedMs: number;
  /** Meteorological convention: the direction the wind blows FROM, in degrees. */
  windFromDeg: number;
  gustMs: number;
  temperatureC: number;
}

export type OpenMeteoResult =
  | { ok: true; data: OpenMeteoConditions }
  | { ok: false; error: string };

interface CurrentBlock {
  wind_speed_10m?: unknown;
  wind_direction_10m?: unknown;
  wind_gusts_10m?: unknown;
  temperature_2m?: unknown;
}

/** A finite number, or null — the feed is external, so nothing about its shape
 *  is trusted until it is checked here at the boundary. */
function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export async function fetchCurrentWeather(
  lat: number,
  lon: number,
  signal?: AbortSignal,
): Promise<OpenMeteoResult> {
  const params = new URLSearchParams({
    latitude: lat.toFixed(4),
    longitude: lon.toFixed(4),
    current: 'wind_speed_10m,wind_direction_10m,wind_gusts_10m,temperature_2m',
    // Metres per second so the numbers drop straight into the m/s fields, no
    // knots/kmh conversion that could silently disagree with the assessment.
    wind_speed_unit: 'ms',
  });

  let response: Response;
  try {
    response = await fetch(`${ENDPOINT}?${params.toString()}`, { signal });
  } catch {
    return { ok: false, error: 'Weather feed unreachable' };
  }

  if (!response.ok) {
    return { ok: false, error: `Weather feed error (HTTP ${response.status})` };
  }

  let body: { current?: CurrentBlock };
  try {
    body = (await response.json()) as { current?: CurrentBlock };
  } catch {
    return { ok: false, error: 'Weather feed sent an unreadable response' };
  }

  const current = body.current;
  const windSpeedMs = finiteNumber(current?.wind_speed_10m);
  const windFromDeg = finiteNumber(current?.wind_direction_10m);
  const gustMs = finiteNumber(current?.wind_gusts_10m);
  const temperatureC = finiteNumber(current?.temperature_2m);

  // Wind, direction and temperature are the fields the assessment turns on; a
  // response missing any of them is not usable, so report it rather than
  // half-filling the form. Gust is genuinely optional (calm air reports none).
  if (windSpeedMs === null || windFromDeg === null || temperatureC === null) {
    return { ok: false, error: 'Weather feed returned no current conditions' };
  }

  return {
    ok: true,
    data: { windSpeedMs, windFromDeg, gustMs: gustMs ?? 0, temperatureC },
  };
}
